// src/pages/admin/AccessRequestsPanel.jsx
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";

import { listAccessRequests, approveRequest, rejectRequest, resendInvite } from "../../api/adminService";

const STATUS_TABS = ["pending", "approved", "rejected", "waitlisted", "all"];

const statusColor = (status) => {
  switch (status) {
    case "pending": return "warning";
    case "approved": return "success";
    case "rejected": return "error";
    case "waitlisted": return "info";
    default: return "default";
  }
};

export default function AccessRequestsPanel() {
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Approve dialog
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveForm, setApproveForm] = useState({ org_name: "", expires_in_days: 7, admin_notes: "" });
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState("");

  // Reject dialog
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectForm, setRejectForm] = useState({ admin_notes: "" });
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectError, setRejectError] = useState("");

  // Resend invite
  const [resendingId, setResendingId] = useState(null);

  const statusFilter = STATUS_TABS[tab];

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listAccessRequests({
        page: page + 1,
        page_size: pageSize,
        status_filter: statusFilter,
      });
      setRows(data.requests || data.items || (Array.isArray(data) ? data : []));
      setTotal(data.total ?? (Array.isArray(data) ? data.length : 0));
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApproveOpen = (row) => {
    setApproveTarget(row);
    setApproveForm({ org_name: row.brokerage || "", expires_in_days: 7, admin_notes: "" });
    setApproveError("");
    setApproveOpen(true);
  };

  const handleApproveSubmit = async () => {
    setApproveLoading(true);
    setApproveError("");
    try {
      await approveRequest(approveTarget.id, approveForm);
      setApproveOpen(false);
      fetchData();
    } catch (e) {
      setApproveError(e.message);
    } finally {
      setApproveLoading(false);
    }
  };

  const handleRejectOpen = (row) => {
    setRejectTarget(row);
    setRejectForm({ admin_notes: "" });
    setRejectError("");
    setRejectOpen(true);
  };

  const handleResendInvite = async (row) => {
    setResendingId(row.id);
    try {
      await resendInvite(row.id);
      fetchData();
    } catch (e) {
      setError(e.message);
    } finally {
      setResendingId(null);
    }
  };

  const handleRejectSubmit = async () => {
    setRejectLoading(true);
    setRejectError("");
    try {
      await rejectRequest(rejectTarget.id, rejectForm);
      setRejectOpen(false);
      fetchData();
    } catch (e) {
      setRejectError(e.message);
    } finally {
      setRejectLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Access Requests
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => { setTab(v); setPage(0); }}
        sx={{ mb: 2 }}
      >
        {STATUS_TABS.map((s) => (
          <Tab key={s} label={s.charAt(0).toUpperCase() + s.slice(1)} />
        ))}
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Market</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Brokerage</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Volume</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No requests found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.full_name}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>
                    {row.city && row.state ? `${row.city}, ${row.state}` : "—"}
                  </TableCell>
                  <TableCell>{row.brokerage || "—"}</TableCell>
                  <TableCell>{row.annual_volume || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.status}
                      color={statusColor(row.status)}
                      size="small"
                      sx={{ fontWeight: 600, textTransform: "capitalize" }}
                    />
                  </TableCell>
                  <TableCell>
                    {row.created_at
                      ? new Date(row.created_at).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {row.status === "pending" && (
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Button
                          size="small"
                          color="success"
                          variant="contained"
                          startIcon={<CheckCircleRoundedIcon />}
                          onClick={() => handleApproveOpen(row)}
                          sx={{ textTransform: "none", fontWeight: 600, fontSize: 12 }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<CancelRoundedIcon />}
                          onClick={() => handleRejectOpen(row)}
                          sx={{ textTransform: "none", fontWeight: 600, fontSize: 12 }}
                        >
                          Reject
                        </Button>
                      </Box>
                    )}
                    {row.status === "approved" && (
                      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                        {row.invite_code && (
                          <Chip
                            label={`Code: ${row.invite_code}`}
                            size="small"
                            variant="outlined"
                            color="success"
                          />
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SendRoundedIcon />}
                          onClick={() => handleResendInvite(row)}
                          disabled={resendingId === row.id}
                          sx={{ textTransform: "none", fontWeight: 600, fontSize: 12 }}
                        >
                          {resendingId === row.id ? "Sending..." : "Resend"}
                        </Button>
                      </Box>
                    )}
                    {row.status === "rejected" && row.admin_notes && (
                      <Typography variant="caption" color="text.secondary">
                        {row.admin_notes}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 20, 50]}
      />

      {/* Approve Dialog */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Request</DialogTitle>
        <DialogContent>
          {approveTarget && (
            <Typography sx={{ mb: 2 }} color="text.secondary">
              Approving <strong>{approveTarget.full_name}</strong> ({approveTarget.email})
            </Typography>
          )}
          {approveError && <Alert severity="error" sx={{ mb: 2 }}>{approveError}</Alert>}
          <TextField
            label="Organization Name"
            value={approveForm.org_name}
            onChange={(e) => setApproveForm((p) => ({ ...p, org_name: e.target.value }))}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Invite Expires in (days)"
            type="number"
            value={approveForm.expires_in_days}
            onChange={(e) => setApproveForm((p) => ({ ...p, expires_in_days: parseInt(e.target.value, 10) || 7 }))}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Admin Notes (optional)"
            value={approveForm.admin_notes}
            onChange={(e) => setApproveForm((p) => ({ ...p, admin_notes: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)}>Cancel</Button>
          <Button
            onClick={handleApproveSubmit}
            variant="contained"
            color="success"
            disabled={approveLoading}
          >
            {approveLoading ? "Approving..." : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Request</DialogTitle>
        <DialogContent>
          {rejectTarget && (
            <Typography sx={{ mb: 2 }} color="text.secondary">
              Rejecting <strong>{rejectTarget.full_name}</strong> ({rejectTarget.email})
            </Typography>
          )}
          {rejectError && <Alert severity="error" sx={{ mb: 2 }}>{rejectError}</Alert>}
          <TextField
            label="Reason / Admin Notes (optional)"
            value={rejectForm.admin_notes}
            onChange={(e) => setRejectForm((p) => ({ ...p, admin_notes: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRejectSubmit}
            variant="contained"
            color="error"
            disabled={rejectLoading}
          >
            {rejectLoading ? "Rejecting..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
